// MACHINERY V2 (Stroje) — the catalog spec-sheet RULES, in pure form (M2.5).
//
// Spec: data package §3 (Specification Tab) "Data-parity" paragraph, which lists
// every rule below by name. Artboards 1006 / 1007 / 1015.
//
// >>> INTENTIONAL DUPLICATE OF components/MachineSpecs.tsx. DO NOT DEDUPLICATE. <<<
// Those helpers are module-private in a 1236-line file whose 4th line is
// `import { Box, ... } from "@mui/material"`. Exporting them would put MUI in
// the v2 import graph, which §3 forbids outright; extracting them to a shared
// module would rewrite a live v1 component, which this wave may not touch. This
// is the same call, for the same reason, as the Wave-1 `V2_DOC_TYPE_KEYS`
// duplicate — and it is why the bodies below are copied VERBATIM rather than
// re-derived: a re-derivation is how two spec sheets start disagreeing about
// which section is prose.
//
// The one thing NOT duplicated is `convertSpec`: utils/unitConversion.ts has no
// imports at all, so the unit maths is imported by the renderer, never re-done.
//
// Everything here is pure — no React, no Redux, no i18n, no MUI.
import { CatalogSpecSection } from "../../../models/models";

/**
 * Detects mojibake (garbled encoding) in a string.
 * Czech text encoded as latin-1 then decoded as UTF-8 produces sequences
 * like Å¾, Ã¡, Ã­, Å™ etc. We look for these common mojibake patterns.
 */
export function hasMojibake(text: string): boolean {
  if (!text) return false;

  // Common mojibake sequences from UTF-8 -> Latin-1 mis-encoding of Czech/Slovak
  const mojibakePatterns = [
    /[\xC3][\x80-\xBF]/,       // Ã followed by high byte (á→Ã¡, í→Ã­, etc.)
    /[\xC5][\x80-\xBF]/,       // Å followed by high byte (ž→Å¾, ř→Å™, etc.)
    /[\xC4][\x80-\xBF]/,       // Ä followed by high byte (č→Ä\x8D, ě→Ä\x9B, etc.)
    /Ã¡|Ã¨|Ã­|Ã©|Ãº|Ã½|Ã¼|Ã¶|Ã¤|Ã®|Ã´|Ã»/, // double-byte mojibake for vowels
    /Å¾|Å™|Å¡|Å¥|Å\u0088|Å¯|Å¤|Å¸/,           // double-byte mojibake for consonants
    /Ä\u008D|Ä\u009B|Ä¾|Ä½|Ä\u0090|Ä\u008E/,   // more Czech/Slovak consonant mojibake
  ];

  // Consecutive non-ASCII byte-like characters are a strong mojibake signal
  const suspiciousChars = text.match(/[\u00C0-\u00FF]{2,}/g);
  if (suspiciousChars && suspiciousChars.length > 1) return true;

  // Many isolated Ã, Å, Ä characters signal mojibake (these are the first byte
  // of a double-byte UTF-8 sequence mis-decoded as Latin-1)
  const isolatedHighBytes = (text.match(/[ÃÅÄ]/g) || []).length;
  if (isolatedHighBytes >= 3) return true;

  for (const pattern of mojibakePatterns) {
    if (pattern.test(text)) return true;
  }
  return false;
}

/**
 * Detects whether a text block is likely Czech language.
 * Czech-specific characters (ě, ř, ů, ň, ď, ť) rarely appear in English or
 * other Western European languages. We also count common Czech diacritics
 * (š, č, ž, ý, á, í, é, ú) and require a minimum density to avoid false
 * positives on e.g. French or German text that shares some accented chars.
 */
export function isCzechText(text: string): boolean {
  if (!text) return false;
  // Characters that are unique to Czech (and Slovak) — very strong signal
  const uniqueCzechMatches = text.match(/[ěřůňďťĚŘŮŇĎŤ]/g);
  if (uniqueCzechMatches && uniqueCzechMatches.length >= 2) return true;

  // Broader set of Czech diacritics — need higher count to distinguish
  // from French/German/Spanish which share some of these
  const allCzechMatches = text.match(/[ěščřžýáíéúůďťňĚŠČŘŽÝÁÍÉÚŮĎŤŇ]/g);
  if (allCzechMatches && allCzechMatches.length >= 3) return true;

  return false;
}

/* -- Section classification ---------------------------------------------- */

/** Section names that are treated as description blocks */
const DESCRIPTION_SECTIONS = new Set([
  "description",
  "description (en)",
  "description (cz)",
  "vertical machining center",
  "horizontal machining center",
  "turning center",
  "cylindrical grinding machine",
  "surface grinding machine",
  "sinker edm",
  "wire edm",
  "multi-axis machining center",
  "5-axis machining center",
  "boring mill",
  "combination lathe",
  "vertical turning center",
  "horizontal turning center",
  "centerless grinding machine",
  "internal grinding machine",
  "tool and cutter grinding machine",
  "jig grinding machine",
  "creep feed grinding machine",
  "profile grinding machine",
  "gear grinding machine",
  "double disk grinding machine",
]);

const EQUIPMENT_SECTIONS = new Set([
  "standard equipment",
  "optional equipment",
]);

/**
 * Additional heuristic: a section is "description-like" if most of its specs
 * have empty keys (i.e., the section is prose, not key-value pairs).
 * This catches machine-type headings that aren't in the static set.
 */
export function isDescriptionLikeSection(section: CatalogSpecSection): boolean {
  if (section.specs.length === 0) return false;
  const emptyKeyCount = section.specs.filter(
    (s) => !s.specKey || s.specKey.trim().length === 0,
  ).length;
  // If more than half the specs have empty keys, treat as description
  return emptyKeyCount > section.specs.length / 2;
}

/**
 * Section ordering hierarchy. Lower number = shown first.
 * Sections not listed get a high default number (shown after everything else).
 */
export function getSectionOrder(sectionName: string): number {
  const lower = sectionName.toLowerCase();

  // Description sections - always first
  if (DESCRIPTION_SECTIONS.has(lower)) return 0;

  // Core parameters
  if (lower === "axes & travels") return 10;
  if (lower === "tooling") return 11;
  if (lower === "feed rates") return 12;
  if (lower === "accuracy") return 13;
  if (lower === "electrical") return 14;
  if (lower === "motors") return 15;
  if (lower === "working capacity") return 16;
  if (lower === "turret") return 17;

  // Spindle data
  if (lower === "spindles" || lower === "spindle(s)") return 20;
  if (lower === "headstock") return 21;
  if (lower === "single spindle") return 22;
  if (lower === "multi spindle") return 23;
  if (lower.includes("grinding wheel") || lower.includes("spindle")) return 24;

  // Work support
  if (lower === "work support" || lower === "table/work support") return 30;
  if (lower === "workpiece envelope") return 31;

  // Control
  if (lower === "control") return 40;
  if (lower === "wire data") return 41;

  // Equipment
  if (lower === "standard equipment") return 50;
  if (lower === "optional equipment") return 51;

  // Options
  if (lower === "options") return 60;

  // Everything else
  return 100;
}

/** Group key for looking up group-specific tokens */

export type GroupKey = "core" | "spindle" | "workSupport" | "control" | "equipment" | "options" | null;

export function getSectionGroupKey(sectionName: string): GroupKey {
  const order = getSectionOrder(sectionName);
  if (order === 0) return null; // descriptions don't show group labels
  if (order >= 10 && order <= 17) return "core";
  if (order >= 20 && order <= 24) return "spindle";
  if (order >= 30 && order <= 31) return "workSupport";
  if (order >= 40 && order <= 41) return "control";
  if (order >= 50 && order <= 51) return "equipment";
  if (order === 60) return "options";
  return null;
}

export function isDescriptionSection(sectionName: string): boolean {
  return DESCRIPTION_SECTIONS.has(sectionName.toLowerCase());
}

export function isEquipmentSection(sectionName: string): boolean {
  return EQUIPMENT_SECTIONS.has(sectionName.toLowerCase());
}

export function isStandardEquipment(sectionName: string): boolean {
  return sectionName.toLowerCase() === "standard equipment";
}
